<template>
  <StepLayout :tracking-id="step.trackingId" :tracking-model="product.model">
    <template #left>
      <div
        class="opacity-100 transition-opacity duration-500 ease-out motion-reduce:transition-none"
        :class="{ 'md:opacity-50': !isFocused }"
      >
        <slot :name="`guidance-${step.id}`" />

        <RevIllustration
          v-if="step.desktopIllustration"
          alt=""
          class="rounded-lg block"
          :height="632"
          :src="step.desktopIllustration"
          :width="776"
        />
      </div>
    </template>

    <template #right>
      <div
        class="opacity-100 transition-opacity duration-500 ease-out motion-reduce:transition-none"
        :class="{ 'md:opacity-50': !isFocused }"
      >
        <h3 class="heading-1 mb-16">
          <FormattedMessage :definition="step.title">
            <template #emphasis>
              <em>
                {{ i18n(step.titleEmphasis) }}
              </em>
            </template>
          </FormattedMessage>
        </h3>

        <slot :name="`description-${step.id}`" />

        <div class="block md:hidden">
          <slot :name="`guidance-${step.id}`" />
        </div>

        <CardGuidance v-if="step.guidance" v-bind="step.guidance" />

        <ul class="list-none" :class="stepOptionsClasses(step)">
          <StepOption
            v-for="(option, index) in options"
            :key="option.trackingValue"
            :index="index"
            :is-group-loading="isGroupLoading"
            :option="option"
            :step="step"
            @changed="onOptionChanged"
            @click="onOptionClick"
          />

          <li v-if="shouldDisplayToggleButton" class="mb-12">
            <RevPicker
              :aria-expanded="displayAllItems"
              class="h-full"
              :label="toggleLabel"
              @click="toggleDisplayAllItems"
            />
          </li>
        </ul>
      </div>
    </template>
  </StepLayout>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import { productAPI } from '@backmarket/http-api'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevIllustration } from '@ds/components/Illustration'
import { RevPicker } from '@ds/components/Picker'

import { type Step } from '../../utils/createCustomizationFunnelData'
import CardGuidance from '../CardGuidance/CardGuidance.vue'
import StepLayout from '../StepLayout/StepLayout.vue'
import StepOption from '../StepOption/StepOption.vue'

import translations from './Step.translations'

const MAX_PICKER_ITEMS = 6

const props = withDefaults(
  defineProps<{
    step: Step
    isFocused?: boolean
    product: productAPI.GetProductResponse
  }>(),
  {
    isFocused: true,
  },
)

const displayAllItems = ref(false)
const isGroupLoading = ref(false)
const i18n = useI18n()

const shouldDisplayToggleButton = computed(() => {
  return props.step.options.length > MAX_PICKER_ITEMS
})

const maximumAmountOfItemsToShow = computed(() => {
  return MAX_PICKER_ITEMS - 1
})

const remainingItemsCount = computed(() => {
  return props.step.options.length - maximumAmountOfItemsToShow.value
})

const options = computed(() => {
  if (shouldDisplayToggleButton.value && !displayAllItems.value) {
    return props.step.options.slice(0, maximumAmountOfItemsToShow.value)
  }

  return props.step.options
})

const toggleLabel = computed(() => {
  return displayAllItems.value
    ? i18n(translations.showLess)
    : i18n(translations.showMore, {
        remaining: remainingItemsCount.value,
      })
})

function stepOptionsClasses(step: Step) {
  if (step.id === 'color') {
    return 'grid grid-cols-2 gap-x-12'
  }

  return ''
}
function toggleDisplayAllItems() {
  displayAllItems.value = !displayAllItems.value
}
function onOptionClick() {
  isGroupLoading.value = true
}
function onOptionChanged() {
  isGroupLoading.value = false
}
</script>
